import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Stack,
  Fade,
} from '@mui/material';
import {
  CheckCircle,
  CalendarToday,
  LocationOn,
  Info,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { useTranslation } from 'react-i18next';

export interface ReproductionData {
  data: {
    count: number;
    entity_type: string;
    animals_processed: number;
    handling_record_confirmation?: {
      message?: string;
      result?: {
        _id?: string;
        _key?: string;
        _rev?: string;
      };
    };
    handling_details_sent: {
      touro_id: string;
    };
    entity_profile: {
      _key: string;
      _id: string;
      _rev: string;
      farm_id: string;
      current_farm_id: string;
      name: string;
      cattle_id?: {
         tag: string;
         country_code: string;
         manufacturer_code: number;
         unique_id: string;
       };
       lot_id?: {
         tag: string;
         country_code: string;
         manufacturer_code: number;
         unique_id: string;
       };
       // Campos específicos para lotes de reprodução (AnimalLots)
       description?: string;
       cattle_tags?: string[];
       is_active?: boolean;
      breed_code: string;
      gender: string;
      birth_date: string;
      birth_weight_kg: number;
      production_purpose: string | null;
      dam_cattle_id: string | null;
      sire_cattle_id: string | null;
      birth_farm_id: string;
      current_paddock_id: string;
      death_date: string | null;
      death_reason: string | null;
      current_status: string;
      created_at: string;
      updated_at: string;
    };
  };
  resumo: string;
  url: string;
}

export interface ReproductionDetailsViewProps {
  reproductionData: ReproductionData;
}

const formatDate = (dateString: string, t?: any): string => {
  try {
    const date = new Date(dateString);
    const locale = t ? (t('common:locale') || 'pt-BR') : 'pt-BR';
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const formatDateTime = (dateString: string, t?: any): string => {
  try {
    const date = new Date(dateString);
    const locale = t ? (t('common:locale') || 'pt-BR') : 'pt-BR';
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const ReproductionDetailsView: React.FC<ReproductionDetailsViewProps> = ({
  reproductionData,
}) => {
  const { t } = useTranslation(['farm', 'common']);
  const { data, resumo } = reproductionData;
  const { entity_profile, handling_details_sent, handling_record_confirmation } = data;

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Fade in timeout={600}>
        <Box>
          {/* Header */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: DESIGN_TOKENS.colors.brand.primary,
                mx: 'auto',
                mb: 2,
                fontSize: '2rem',
              }}
            >
              <img 
                src="/touro.svg" 
                alt="Touro" 
                style={{ 
                  width: '2rem', 
                  height: '2rem', 
                  filter: 'brightness(0) invert(1)' // Makes SVG white
                }} 
              />
            </Avatar>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: DESIGN_TOKENS.colors.brand.primary,
                mb: 1 
              }}
            >
              {t('farm:reproductionDetails.title')}
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: DESIGN_TOKENS.colors.text.primary,
                fontWeight: 600,
                mb: 2
              }}
            >
              {resumo}
            </Typography>
            
            {/* Status Chip */}
            <Chip
              icon={<CheckCircle />}
              label={t('farm:reproductionDetails.inseminationConfirmed')}
              color="success"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 2,
                py: 1,
              }}
            />
          </Box>

          {/* Cards Container */}
          <Stack spacing={3}>
            {/* Animal Information Card */}
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                border: `1px solid ${DESIGN_TOKENS.colors.brand.surface}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <img 
                    src="/vaca.png" 
                    alt={entity_profile.cattle_tags ? t('farm:reproductionDetails.reproductionLot') : (data.entity_type === 'animal' ? t('farm:reproductionDetails.cow') : t('farm:reproductionDetails.lot'))} 
                    style={{ 
                      width: '24px', 
                      height: '24px', 
                      marginRight: '8px'
                    }} 
                  />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.brand.primary 
                    }}
                  >
                    {entity_profile.cattle_tags ? t('farm:reproductionDetails.reproductionLotInfo') : (data.entity_type === 'animal' ? t('farm:reproductionDetails.femaleInfo') : t('farm:reproductionDetails.lotInfo'))}
                  </Typography>
                </Box>
                
                <Stack spacing={2}>
                  {/* Para lotes de reprodução, mostra apenas nome e animais */}
                  {entity_profile.cattle_tags ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t('farm:reproductionDetails.lotName')}:
                        </Typography>
                        <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                          {entity_profile.name}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {t('farm:reproductionDetails.animalsInLot')} ({entity_profile.cattle_tags.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {entity_profile.cattle_tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              sx={{ 
                                bgcolor: DESIGN_TOKENS.colors.brand.surface,
                                color: DESIGN_TOKENS.colors.brand.primary
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </>
                  ) : (
                    /* Para animais individuais, mostra todos os campos */
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t('farm:reproductionDetails.name')}:
                        </Typography>
                        <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                          {entity_profile.name}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t('farm:reproductionDetails.tagIdentification')}:
                        </Typography>
                        <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                          {entity_profile.cattle_id?.tag || 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t('farm:reproductionDetails.breed')}:
                        </Typography>
                        <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                          {entity_profile.breed_code}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t('farm:reproductionDetails.birthDate')}:
                        </Typography>
                        <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                          {formatDate(entity_profile.birth_date, t)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t('farm:reproductionDetails.birthWeight')}:
                        </Typography>
                        <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                          {entity_profile.birth_weight_kg} kg
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t('farm:reproductionDetails.currentPaddock')}:
                        </Typography>
                        <Chip
                          icon={<LocationOn />}
                          label={`${t('farm:reproductionDetails.paddock')} ${entity_profile.current_paddock_id}`}
                          size="small"
                          sx={{ bgcolor: DESIGN_TOKENS.colors.brand.surface }}
                        />
                      </Box>
                      
                      {entity_profile.is_active !== undefined && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t('farm:reproductionDetails.status')}:
                          </Typography>
                          <Chip
                            label={entity_profile.is_active ? t('farm:reproductionDetails.active') : t('farm:reproductionDetails.inactive')}
                            size="small"
                            sx={{ 
                              bgcolor: entity_profile.is_active ? DESIGN_TOKENS.colors.status.success[100] : DESIGN_TOKENS.colors.neutral[100],
                              color: entity_profile.is_active ? DESIGN_TOKENS.colors.status.success[700] : DESIGN_TOKENS.colors.neutral[700]
                            }}
                          />
                        </Box>
                      )}
                      
                      {entity_profile.description && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t('farm:reproductionDetails.description')}:
                          </Typography>
                          <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                            {entity_profile.description}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Reproduction Details Card */}
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                border: `1px solid ${DESIGN_TOKENS.colors.brand.surface}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <img 
                    src="/touro.svg" 
                    alt={t('farm:reproductionDetails.bull')} 
                    style={{ 
                      width: '24px', 
                      height: '24px', 
                      marginRight: '8px',
                      fill: DESIGN_TOKENS.colors.brand.primary
                    }} 
                  />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.brand.primary 
                    }}
                  >
                    {t('farm:reproductionDetails.reproductionDetailsTitle')}
                  </Typography>
                </Box>
                
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {t('farm:reproductionDetails.bullUsed')}:
                    </Typography>
                    <Chip
                      label={handling_details_sent.touro_id}
                      sx={{ 
                        bgcolor: DESIGN_TOKENS.colors.brand.primary,
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {t('farm:reproductionDetails.procedureType')}:
                    </Typography>
                    <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                      {t('farm:reproductionDetails.artificialInsemination')}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {t('farm:reproductionDetails.animalsProcessed')}:
                    </Typography>
                    <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                      {data.animals_processed || 0}
                    </Typography>
                  </Box>
                  

                </Stack>
              </CardContent>
            </Card>

            {/* Confirmation Card */}
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                border: `1px solid ${DESIGN_TOKENS.colors.status.success[100]}`,
                bgcolor: DESIGN_TOKENS.colors.status.success[50],
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircle sx={{ color: DESIGN_TOKENS.colors.status.success[600], mr: 1 }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.status.success[600] 
                    }}
                  >
                    {t('farm:reproductionDetails.systemConfirmation')}
                  </Typography>
                </Box>
                
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('farm:reproductionDetails.status')}:
                    </Typography>
                    <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                      {handling_record_confirmation?.message || t('farm:reproductionDetails.waitingConfirmation', 'Aguardando confirmação...')}
                    </Typography>
                  </Box>
                  
                  <Divider />
                  
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {t('farm:reproductionDetails.recordId')}:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: DESIGN_TOKENS.colors.text.secondary,
                        fontFamily: 'monospace',
                        bgcolor: DESIGN_TOKENS.colors.background.paper,
                        p: 1,
                        borderRadius: 1,
                        border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`
                      }}
                    >
                      {handling_record_confirmation?.result?._id || '-'}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Additional Info */}
            <Box sx={{ 
              p: 2, 
              bgcolor: DESIGN_TOKENS.colors.background.elevated,
              borderRadius: 2,
              border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Info sx={{ color: DESIGN_TOKENS.colors.brand.primary, mr: 1, fontSize: '1.2rem' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.brand.primary }}>
                  {t('farm:reproductionDetails.importantInfo')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                {t('farm:reproductionDetails.importantInfoText')}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Fade>
    </Box>
  );
};

export default ReproductionDetailsView;